<template>
  <div class="experiment-phase">
    <Alert
      v-if="isEditMode && hasMissingFields"
      class="experiment-phase__banner"
      :text="missingFieldsText"
      :vuetifyProps="{
        color: 'yellow',
        icon: bannerIcon,
      }"
    />
    <div class="experiment-phase__content">
      <PhaseSection
        :title="$t('common.rev_state.do')"
        :description="$t('rev_cycle.experiment.description')"
        :isEditMode="isEditMode"
        :hasNextButton="hasNextButton"
        :hasBackButton="hasBackButton"
        @next="$emit('next')"
        @back="$emit('back')"
      >
        <MediaContainer
          data-private
          id="experimentPhoto"
          slot="left"
          class="mt-6"
          :images="media"
          :readOnly="!isEditMode || isSaving"
          @change="handleMediaChange"
        />
        <TranslationLoading slot="right" v-if="isTranslating" />
        <div slot="right" v-else>
          <p class="d-flex justify-end caption blue-grey--text mb-6">
            * {{ $t('common.required_fields') }}
          </p>
          <TextQuestion
            v-model="whatDidYouDo"
            data-private
            rows="7"
            tid="improvementIdea-textarea-experiment"
            tourId="experiment-textArea-whatDidYouDo"
            :maxLength="$constants.improvementIdea.whatDidYouDoMaxLength"
            :question="`${$t('rev_cycle.experiment.what_did_you_do')}*`"
            :error="markRequiredFields && !whatDidYouDo"
            :placeholder="
              $t('rev_cycle.experiment.what_did_you_do_textarea_placeholder')
            "
            :isEditMode="isEditMode"
            @focusOut="trackEvent('whatDidYouDo')"
          />
        </div>
      </PhaseSection>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import _ from 'lodash'

import revPermissionsMixin from '@/mixins/revPermissionsMixin'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import PhaseSection from '@/components/Rev/PhaseSection/PhaseSection.vue'
import MediaContainer from '@/components/MediaContainer/MediaContainer.vue'
import Alert from '@/components/Alert'
import TextQuestion from '@/components/Rev/TextQuestion.vue'

import addCoachIcon from '@/assets/rev/add-coach.svg'
import TranslationLoading from '@/components/Rev/TranslationLoading.vue'

export default {
  name: 'Experiment',

  mixins: [trackerMixin, revPermissionsMixin],

  props: {
    isEditMode: {
      type: Boolean,
      required: true,
    },
    isRelateComplete: {
      type: Boolean,
      required: true,
    },
    rev: {
      type: Object,
      required: true,
    },
    isSaving: Boolean,
    isTranslating: Boolean,
    hasBackButton: Boolean,
    hasNextButton: Boolean,
    markRequiredFields: Boolean,
  },

  components: {
    PhaseSection,
    Alert,
    MediaContainer,
    TextQuestion,
    TranslationLoading,
  },

  data() {
    return {
      addCoachIcon,
    }
  },

  computed: {
    ...mapGetters(['account']),

    permissions() {
      return this.getPermissions({
        rev: this.rev,
        account: this.account,
      })
    },

    whatDidYouDo: {
      get() {
        return this.rev.experiment.whatDidYouDo
      },
      set(whatDidYouDo) {
        this.$emit('revChanged')
        this.updateWhatDidYouDo(whatDidYouDo)
      },
    },

    media() {
      return this.rev?.experiment?.media ?? []
    },

    missingFieldsText() {
      if (!this.isCoachAssigned) return this.$t('rev.missing_coach_alert')

      return this.$t('rev.missing_fields_alert')
    },

    bannerIcon() {
      if (!this.isCoachAssigned) return 'mdi-account-plus'
      return 'mdi-information'
    },

    owner() {
      return this.rev.team.owner
    },

    coach() {
      return _.get(this.rev.team, 'coach', {})
    },

    isCoachAssigned() {
      return !_.isEmpty(this.coach) || this.permissions.isOwnerCoachOrAdmin
    },

    isAdminOrCoach() {
      return this.permissions.isOwnerCoachOrAdmin
    },

    hasMissingFields() {
      return (
        (!this.isCoachAssigned && !this.isAdminOrCoach) ||
        !this.isRelateComplete
      )
    },
  },

  methods: {
    ...mapMutations({
      updateWhatDidYouDo: 'improvementIdea/updateWhatDidYouDo',
      setExperimentMedia: 'improvementIdea/setExperimentMedia',
    }),

    trackEvent(changedField) {
      this.trackUpdate(this.rev, changedField)
    },

    handleMediaChange(media) {
      this.setExperimentMedia(media)
      this.trackEvent('experimentPhotos')
      this.$emit('revChanged')
    },
  },
}
</script>

<style lang="scss" scoped>
.experiment-phase {
  &__banner {
    margin-bottom: 24px;
  }

  &__content {
    display: flex;
  }
}
</style>
